/**
 * Display order for room messages.
 *
 * Python posts the assistant's audience chart last in its round, after every
 * twin reply (no twin reacts to it), and Nest completes the turn only once it
 * landed. This placement stays as a guard for rows stored before that change
 * or delivered out of order: at render time each assistant row moves to right
 * after the last row of the same `metadata.responseTurnId`. Rows without a
 * turn id stay put.
 * Reactions hang off `messageSequenceIndex`, so they are unaffected.
 */

import { ChatRoomMessageDto } from '@digital-twins/shared-types'

export interface AssistantPlacementAccessors<T> {
  isAssistant: (row: T) => boolean
  turnIdOf: (row: T) => string | null | undefined
  /** Rows that may not anchor an assistant row (e.g. unacknowledged sends). */
  isAnchorable?: (row: T) => boolean
}

/**
 * Move every assistant row to right after the last anchorable, non-assistant
 * row of its turn. Stable: rows keep their relative order otherwise, and
 * several assistant rows of one turn keep theirs. Never mutates `rows`.
 */
export function placeAssistantLastInTurn<T>(
  rows: Array<T>,
  accessors: AssistantPlacementAccessors<T>,
): Array<T> {
  const { isAssistant, turnIdOf, isAnchorable = () => true } = accessors

  const lastAnchorByTurn = new Map<string, number>()
  rows.forEach((row, index) => {
    const turnId = turnIdOf(row)
    if (turnId && !isAssistant(row) && isAnchorable(row)) {
      lastAnchorByTurn.set(turnId, index)
    }
  })

  const heldByAnchor = new Map<number, Array<T>>()
  const out: Array<T> = []
  rows.forEach((row, index) => {
    const turnId = isAssistant(row) ? turnIdOf(row) : null
    const anchor = turnId ? lastAnchorByTurn.get(turnId) : undefined
    if (anchor !== undefined && anchor > index) {
      const held = heldByAnchor.get(anchor) ?? []
      held.push(row)
      heldByAnchor.set(anchor, held)
      return
    }
    out.push(row)
    const released = heldByAnchor.get(index)
    if (released) {
      out.push(...released)
    }
  })
  return out
}

function responseTurnIdOf(message: ChatRoomMessageDto): string | null {
  const metadata = message.metadata as Record<string, unknown> | null
  const turnId = metadata?.responseTurnId
  return typeof turnId === 'string' && turnId.length > 0 ? turnId : null
}

/**
 * Canonical history by sequence, unacknowledged sends (negative sequence) last,
 * then the assistant's rows last in their turn. Never mutates the input.
 */
export function orderRoomMessages(messages: Array<ChatRoomMessageDto>): Array<ChatRoomMessageDto> {
  const sorted = messages.slice().sort((a, b) => {
    const aPending = a.sequenceIndex < 0
    const bPending = b.sequenceIndex < 0
    if (aPending || bPending) {
      return Number(aPending) - Number(bPending)
    }
    return a.sequenceIndex - b.sequenceIndex
  })
  return placeAssistantLastInTurn(sorted, {
    isAssistant: (message) => message.senderType === 'george',
    turnIdOf: responseTurnIdOf,
    isAnchorable: (message) => message.sequenceIndex >= 0,
  })
}
